import { MouseEvent, useState } from 'react'
import { useStyletron } from 'baseui'
import { StatefulTooltip } from 'baseui/tooltip'
import { useTranslation } from 'react-i18next'
import copyToClipboard from '@/shared/tools/copyToClipboard'
import usePulseScope from '@/shared/components/PulseScope/usePulseScope'
import { pulseTooltipOverrides } from '@/shared/components/PulseScope/styled-components'
import { PulseCopyButton } from './styled-components'

interface IconProps {
  size: number
}

const ClipboardIcon = ({ size }: IconProps) => (
  <svg viewBox='0 0 24 24' width={size} height={size} fill='currentColor' aria-hidden='true'>
    <path d='M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z' />
  </svg>
)

const CheckIcon = ({ size }: IconProps) => (
  <svg viewBox='0 0 32 32' width={size} height={size} fill='currentColor' aria-hidden='true'>
    <polygon points='14 21.414 9 16.413 10.413 15 14 18.586 21.585 11 23 12.415 14 21.414' />
  </svg>
)

interface CopyEndEnhancerProps {
  value: string | null | undefined
  stripSpaces?: boolean
}

const CopyEndEnhancer = ({ value, stripSpaces = false }: CopyEndEnhancerProps) => {
  const [, theme] = useStyletron()
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const isPulse = usePulseScope()

  const handleCopy = async (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!value) return
    const textToCopy = stripSpaces ? value.replace(/\s/g, '') : value
    if (!(await copyToClipboard(textToCopy))) return
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  if (isPulse) {
    return (
      <StatefulTooltip
        content={copied ? t('common:copied') : t('common:copy')}
        returnFocus
        autoFocus
        overrides={pulseTooltipOverrides}
      >
        <PulseCopyButton type='button' onClick={handleCopy} $copied={copied} $hasValue={!!value}>
          {copied ? <CheckIcon size={16} /> : <ClipboardIcon size={16} />}
        </PulseCopyButton>
      </StatefulTooltip>
    )
  }

  return (
    <StatefulTooltip content={copied ? t('common:copied') : t('common:copy')} returnFocus autoFocus>
      <button
        type='button'
        onClick={handleCopy}
        style={{
          background: 'none',
          border: 'none',
          cursor: value ? 'pointer' : 'default',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: value ? 1 : 0.35,
          color: copied ? 'green' : theme.colors.primaryA,
        }}
      >
        {copied ? <CheckIcon size={18} /> : <ClipboardIcon size={18} />}
      </button>
    </StatefulTooltip>
  )
}

export default CopyEndEnhancer
